<template>
  <div>
    <popup-hint hint="otherequipment">
      <template v-slot:breadcrumbs>
        <breadcrumbs home="Other equipment">
          <template v-slot:left v-if="maintenanceDueCount">
            <list-notification v-model="showStatus">
              <v-list-item>
                <v-icon>mdi-arrow-right-thin</v-icon>
                  {{maintenanceDueCount}} due for maintenance
                <v-spacer/>
                <v-btn small color="warning" @click="showStatus=false;$nextTick(() => {filters.siteId=null;filters.status='1';;filters.maintenanceDue='1';filters.typeId='0'})">View</v-btn>
              </v-list-item>
            </list-notification>
          </template>
        </breadcrumbs>
      </template>
      <template v-slot:top>
        Other equipment can be defined to track maintenance and to use as resources for scheduling.
      </template>
      <template v-slot:actions="{close}">
        <v-btn color="primary" class="ml-4" small @click="close();$refs.import.doImport()">Import</v-btn>
        <v-btn @click="close()" :to="{name:'EquipmentOtherEditNew'}" color="primary" class="ml-4" small>Create</v-btn>
      </template>
    </popup-hint>

    <import-csv v-model="doImport" type="other equipment" :fields="importFields" importAction="otherEquipmentsImport" :instructions="instructions" ref="import" @refresh="$refs.table.buildList()"/>

    <purtrak-table name="equipment" objectType="other equipment" :defaultColumns="defaultColumns" :headers="headers" ref="table" show-select show-expand
                   listEndPoint="otherEquipments2" v-model="selected" @list="listBuilt"
                   fields="id,name,site{id,name},active,pictureURL,equipmentType{id,name},manufacturer,modelNumber,serialNumber,maintenanceDue"
                   :listFilters="listFilters"
                   @dblclick="equipment => $router.push({name:'EquipmentOtherEdit',params:{equipmentId:equipment.id}})"
                   @itemExpanded="({item, value}) => expand(item, value)"
                   :item-class="equipment => (equipment.active ? '' : 'grey--text')"
                   title="Other equipment"
                   :print-info="{subTitle:[filters.maintenanceDue=='1'?'Maintenance due':'',filters.status!='1'?(filters.status=='0'?'Inactive':'All'):'',
                                           filters.siteId!=null?siteName:'',filters.typeId!='0'?typeName:'']}"
                   download-endpoint="otherEquipmentsExportCSV">
      <template v-slot:filter>
        <scan-code @code="scan" return-unmatched-code auto-close/>
        <filter-menu include-reset @reset="filters.siteId=null;filters.status='1';filters.maintenanceDue='0';filters.typeId='0';filters.id=null">
          <v-list-item>
            <filter-search label="Name" v-model="filters.search"/>
          </v-list-item>
          <v-list-item class="mt-n5">
            <v-checkbox v-model="filters.maintenanceDue" true-value="1" false-value="0" label="Maintenance due"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Status" dense outlined :items="[{value:'2',text:'All'},{divider:true},{value:'1',text:'Active'},{value:'0',text:'Inactive'}]" v-model="filters.status"/>
          </v-list-item>
          <v-list-item v-if="sites.length>1">
            <v-autocomplete label="Site" dense outlined :items="[{id:null,name:'All'},{divider:true},...sites]" v-model="filters.siteId" item-text="name" item-value="id"/>
          </v-list-item>
          <v-list-item>
            <v-autocomplete label="Type" dense outlined :items="[{id:'0',name:'All'},{divider:true},...types]" v-model="filters.typeId" item-text="name" item-value="id"/>
          </v-list-item>
        </filter-menu>
        <template v-if="!$vuetify.breakpoint.mobile">
          <v-chip v-show="filters.id" close small color="primary" class="ml-1" @click:close="filters.id=null">Scanned other equipment</v-chip>
          <v-chip v-show="filters.search" close small class="ml-1" @click:close="filters.search=''">Still: {{filters.search}}</v-chip>
          <v-chip v-show="filters.maintenanceDue=='1'" close small class="ml-1" @click:close="filters.maintenanceDue='0'">Maintenance due</v-chip>
          <v-chip v-show="filters.status!='1'" close small class="ml-1" @click:close="filters.status='1'">{{filters.status=='0'?'Inactive':'All'}}</v-chip>
          <v-chip v-show="filters.siteId!=null" close small class="ml-1" @click:close="filters.siteId=null">{{siteName}}</v-chip>
          <v-chip v-show="filters.typeId!='0'" close small class="ml-1" @click:close="filters.typeId='0'">{{typeName}}</v-chip>
        </template>
      </template>
      <template v-slot:menu>
        <template v-if="!readOnly">
          <v-list-item :to="{name:'EquipmentOtherEditNew'}"><v-list-item-title>Create other equipment</v-list-item-title></v-list-item>
          <v-list-item @click="doImport=true" v-if="!$vuetify.breakpoint.mobile"><v-list-item-title>Import other equipment from CSV</v-list-item-title></v-list-item>
          <v-divider/>
        </template>
        <v-list-item :to="{name:'EquipmentTypes'}"><v-list-item-title>Equipment types</v-list-item-title></v-list-item>
      </template>

      <template v-slot:footer v-if="selected.length">
        <v-icon style="position: absolute" class="mt-3 ml-3">mdi-arrow-up-left</v-icon>
        <equipment-other-batch-edit :equipment="selected" @refresh="$refs.table.buildList($event)"/>
      </template>
      <template v-slot:item.name="{item,printing}">
        <template v-if="printing">{{item.name}}</template>
        <template v-else>
          <v-hover v-if="!readOnly" v-slot="{hover}">
            <a @click.stop="$router.push({name:'EquipmentOtherEdit',params:{equipmentId:item.id}})"
              :class="{'text-decoration-underline':hover,'red--text':item.maintenanceDue,'grey--text':!item.active}">{{item.name}}</a>
          </v-hover>
          <div v-else :class="{'red--text':item.maintenanceDue,'grey--text':!item.active}">{{item.name}}</div>
          <div v-if="item.maintenanceDue" class="grey--text text-caption">
            <v-btn plain color="error" x-small :to="{name:'EquipmentMaintenance',params:{equipmentId:item.id}}" class="px-0">
              Maintenance due
            </v-btn>
          </div>
          <div v-else-if="!item.active" class="grey--text text-caption">Inactive</div>
        </template>
      </template>
      <template v-slot:item.maintenanceDue="{value}">
        {{value | formatDate}}
      </template>
      <template v-slot:header.action v-if="!readOnly">
        <v-btn fab color="primary" small :to="{name:'EquipmentOtherEditNew'}"><v-icon>mdi-plus</v-icon></v-btn>
      </template>
      <template v-slot:item.action="{item}" v-if="!readOnly">
        <v-menu :nudge-width="220" offset-y :nudge-left="150">
          <template v-slot:activator="{ on }">
            <v-btn small v-on="on" v-if="$vuetify.breakpoint.mobile" outlined>Equipment operations</v-btn>
            <v-btn small icon v-on="on" v-else><v-icon small >mdi-dots-vertical</v-icon></v-btn>
          </template>
          <v-list dense>
            <template v-if="company.allowAudit && company.permissions.includes('audit')" >
              <v-list-item :to="{name:'Audit',query:{entityId:item.id}}"><v-list-item-title>Audit history</v-list-item-title></v-list-item>
              <v-divider/>
            </template>
            <v-menu :nudge-left="240">
              <template v-slot:activator="{on:reduce}">
                <v-list-item v-on="reduce">
                  <v-list-item-title>Maintenance</v-list-item-title>
                  <v-list-item-action><v-icon>mdi-chevron-right</v-icon></v-list-item-action>
                </v-list-item>
              </template>
              <v-list dense>
                <v-list-item v-if="!readOnly" :to="{name:'EquipmentMaintenance',params:{equipmentId:item.id}}"><v-list-item-title>Perform maintenance</v-list-item-title></v-list-item>
                <v-list-item :to="{name:'EquipmentMaintenanceLog',params:{equipmentId:item.id}}"><v-list-item-title>Maintenance history</v-list-item-title></v-list-item>
              </v-list>
            </v-menu>
            <v-divider/>
            <generate-qr-code type="OtherEquipment" :id="item.id"/>
            <v-list-item :to="{name:'EquipmentOtherEdit',params:{equipmentId:item.id}}"><v-list-item-title>Edit equipment</v-list-item-title></v-list-item>
            <v-list-item @click="equipmentToDelete=item;showDelete=true"><v-list-item-title>Delete equipment</v-list-item-title></v-list-item>
          </v-list>
        </v-menu>
      </template>
      <template v-slot:expanded-item="{ headers,item }">
        <equipment-list-expand :width="headers.length" :equipment="item"/>
      </template>
    </purtrak-table>

    <delete-object v-model="showDelete" :name="equipmentToDelete.name" @delete="performDeleteEquipment"/>
  </div>
</template>

<script>
import EquipmentListExpand from '@/views/equipment/EquipmentListExpand'
import EquipmentOtherBatchEdit from '@/views/equipment/other/EquipmentOtherBatchEdit'
import generateQrCode from '@/components/GenerateQrCode'

export default {
  name: 'EquipmentOther',
  components: {EquipmentListExpand,EquipmentOtherBatchEdit,generateQrCode},
  data() {
    return {
      maintenanceDueCount: 0,
      showStatus: false,
      filters: {
        maintenanceDue: '0',
        status: '1',
        siteId: null,
        typeId: '0',
        search: '',
        id: null
      },
      types: [{id:null,name:'Other'},{divider:true}],
      equipmentToDelete: {},
      showDelete: false,
      doImport: false,
      importFields: [{name:'Name',mapTo:'',mandatory:true},{name:'Type',mapTo:'',mandatory:false}],
      instructions: `<ol class="mb-4">
                      <li>Name</li>
                      <li>Type (optional)</li>
                      </ol>`,
      defaultColumns: [{ value: 'name', visible:true},
                       { value: 'equipmentType.name', visible:true},
                       { value: 'site.name', visible:true},
                       { value: 'maintenanceDue', visible:false}],
      headers: [{ text: 'NAME', value: 'name', width:'29%',sortable:true},
                { text: 'TYPE', value: 'equipmentType.name', width:'20%',sortable:true},
                { text: 'MAINTENANCE', value: 'maintenanceDue', width:'15%',sortable:true},
                { text: 'SITE', value: 'site.name', width:'45%',sortable:true,omit:() => this.sites.length <= 1}]
    }
  },
  computed: {
    company() {
      return this.$store.getters.company;
    },
    sites() {
      return this.$store.getters.sites(true);
    },
    siteName() {
      return this.sites.find(site => site.id === this.filters.siteId)?.name;
    },
    typeName() {
      return this.types.find(type => type.id === this.filters.typeId)?.name;
    },
    readOnly() {
      return !this.company.permissions.includes('production.write');
    },
    listFilters() {
      const filters = [];
      if (this.filters.id)
        filters.push({field:'id', comparator:'eq', value:this.filters.id});
      if (this.filters.maintenanceDue == '1')
        filters.push({field:'maintenanceDue', comparator:'ne', value:'null'});
      if (this.filters.status == '1' || this.filters.status == '0')
        filters.push({field:'active', comparator:'eq', value:this.filters.status == '1' ? 'true' : 'false'});

      if (this.filters.siteId)
        filters.push({field:'siteId', comparator:'eq', value:this.filters.siteId});

      if (this.filters.typeId == null)
        filters.push({field:'typeId', comparator:'isNull', value:''});
      else if (this.filters.typeId !== '0')
        filters.push({field:'typeId', comparator:'eq', value:this.filters.typeId});

      if (this.filters.search)
        filters.push({field:'name', comparator:'contains', value:this.filters.search});

      return filters;
    }
  },
  async created() {
    this.$store.dispatch('checkTasks');
    this.$lazyListLoad(this.types, 'equipmentTypes2', 'id,name', 100);

    // Maintenance due?
    const results = await this.$apiCall(`query EquipmentStills {
                                            equipmentMaintenanceDue{typeName}
                                          }`,
                                      {});
    if (!results.error)
      this.maintenanceDueCount = results.data.equipmentMaintenanceDue.filter(e => !['Tank','Fermenter','Still'].includes(e.typeName)).length;

    if (!['EquipmentOtherEdit', 'EquipmentOtherEditNew'].includes(this._previousRoute) &&
        Object.keys(this.$route.query).length === 0 &&
        this.maintenanceDueCount && !localStorage.getItem('hideNotification')) {
      this.$nextTick(() => this.showStatus = true);
      setTimeout(() => this.showStatus = false, 10_000);  // Close in 10 seconds
    }
  },
  methods: {
    scan({type, id}) {
      if (type === 'OtherEquipment')
        Object.assign(this.filters, {siteId:null,status:'2',maintenanceDue:'0',typeId:'0',id});
    },
    listBuilt({list}) {
      if (this.filters.id && list.edges.length === 1)
        this.$refs.table.expanded.push(list.edges[0].node);
    },
    async expand(item, value) {
      if (value && !item.maintenance) {
        this.loading = true;
        const results = await this.$apiCall(`query EquipmentOther($id:ID!) {
                                                  otherEquipment(id:$id) {
                                                    maintenance {frequency,frequencyUnits,nextDate,description}
                                                  }
                                                }`,
                                            {id:item.id});
        this.$set(item, 'maintenance', results.data.otherEquipment.maintenance);
        this.loading = false;
      }
    },
    async performDeleteEquipment() {
      this.loading = true;
      const results = await this.$apiCall(`mutation EquipmentOther($id: ID!) {
                                              objectDelete(id:$id)
                                            }`,
                                          {id:this.equipmentToDelete.id});
      this.loading = false;
      if (!results.error) {
        this.showDelete = false;
        this.$refs.table.buildList();
        this.$store.commit('setError', {type:'success',message:`Deleted equipment ${this.equipmentToDelete.name}`});
      }
    },
  }
}
</script>